import { sanitizeHtml } from '@realadvisor/html-sanitizer';
import Quill from 'quill';
import { renderToStaticMarkup } from 'react-dom/server';

import { isLocalhost } from '@shared/config';

import { isResolvedBrochureSuggestion } from './brochureSuggestion';
import { resolveDocsTrackingMarkersForPreview } from './docsTrackingPreview';
import { escapeAttr, escapeText } from './draftConvertCore';
import { renderPropertySnippet } from './renderPropertySnippet';
import type {
  EmailEditorRenderSnippetProps,
  EmailEditorSnippetData,
} from './types';

const BlockEmbed = Quill.import('blots/block/embed') as any;
const InlineEmbed = Quill.import('blots/embed') as any;

const blockEmbedHtml = (domNode: HTMLElement, attr: string): string => {
  return `<div ${attr}="${escapeAttr(
    domNode.getAttribute(attr) ?? '',
  )}"></div>`;
};

export type SnippetRemoveHandler = (node: HTMLElement) => void;

const defaultDisplayT = (
  key: string,
  options?: Record<string, string | number | null>,
): string => (key === 'roomsShort' ? `${options?.count ?? 0} rooms` : key);

export const SNIPPET_REMOVE_CLASS = 'ra-snippet-remove';

// Lucide `X`, inlined: the preview is plain DOM, outside React's tree.
const X_ICON =
  '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" ' +
  'viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
  'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
  '<path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>';

// Removing the card must not depend on the caret: it is `contenteditable=false`
// and the caret can only reach it from the paragraph that follows.
const appendRemoveButton = (
  node: HTMLElement,
  t: EmailEditorRenderSnippetProps['t'],
  onRemove: SnippetRemoveHandler,
): void => {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = SNIPPET_REMOVE_CLASS;
  button.setAttribute('contenteditable', 'false');
  button.setAttribute('aria-label', t('Remove'));
  button.title = t('Remove');
  button.innerHTML = X_ICON;
  // Keep the editor selection intact: a focus steal here would drop the range
  // Quill needs to apply the deletion.
  button.addEventListener('mousedown', event => event.preventDefault());
  button.addEventListener('click', event => {
    event.preventDefault();
    event.stopPropagation();
    onRemove(node);
  });
  node.appendChild(button);
};

const renderSnippetPreview = (
  node: HTMLElement,
  t: EmailEditorRenderSnippetProps['t'] = defaultDisplayT,
  language = 'en',
  onRemove?: SnippetRemoveHandler,
): void => {
  const value = node.getAttribute('data-ra-snippet') ?? '';
  const data = JSON.parse(decodeURIComponent(value)) as EmailEditorSnippetData;
  // `renderPropertySnippet` also feeds `expandMarkers`, which builds the HTML
  // of the email that is actually sent - nothing editor-only may live in it.
  node.innerHTML = resolveDocsTrackingMarkersForPreview(
    renderToStaticMarkup(
      renderPropertySnippet({ ...data, t, language, emailMode: true }),
    ),
    isLocalhost,
  );
  if (onRemove != null) {
    appendRemoveButton(node, t, onRemove);
  }
};

export const refreshEditorPreviews = (
  root: ParentNode,
  t?: EmailEditorRenderSnippetProps['t'],
  language?: string,
  onRemoveSnippet?: SnippetRemoveHandler,
): void => {
  root
    .querySelectorAll<HTMLElement>('div[data-ra-snippet]')
    .forEach(node => renderSnippetPreview(node, t, language, onRemoveSnippet));
  refreshBrochureLabels(root, t);
};

export const refreshBrochureLabels = (
  root: ParentNode,
  t?: EmailEditorRenderSnippetProps['t'],
): void => {
  const label = (t ?? defaultDisplayT)('Brochure');
  root.querySelectorAll<HTMLElement>('.ra-brochure-label').forEach(node => {
    if (node.textContent !== label) {
      node.textContent = label;
    }
  });
};

export class SnippetBlot extends BlockEmbed {
  static blotName = 'ra-snippet';
  static tagName = 'DIV';
  static className = 'ra-snippet';

  static create(value: string): HTMLElement {
    const node = super.create(value) as HTMLElement;
    node.setAttribute('data-ra-snippet', value);
    node.setAttribute('contenteditable', 'false');
    renderSnippetPreview(node);
    return node;
  }

  static value(node: HTMLElement): string {
    return node.getAttribute('data-ra-snippet') ?? '';
  }

  html(): string {
    return blockEmbedHtml(this.domNode as HTMLElement, 'data-ra-snippet');
  }
}

type VarValue = { id: string; display: string };

export const getVarDisplay = (
  node: HTMLElement,
  contentNode?: Node | null,
): string =>
  node.getAttribute('data-ra-brochure-url') ??
  contentNode?.textContent ??
  node.textContent ??
  '';

export class VarBlot extends InlineEmbed {
  static blotName = 'ra-var';
  static tagName = 'SPAN';
  static className = 'ra-var';

  static create(value: VarValue): HTMLElement {
    const node = super.create(value) as HTMLElement;
    node.setAttribute('data-ra-var', value.id);
    node.setAttribute('contenteditable', 'false');
    if (isResolvedBrochureSuggestion(value.id, value.display)) {
      node.setAttribute('data-ra-brochure-url', value.display);
      const label = document.createElement('span');
      label.className = 'ra-brochure-label';
      label.textContent = 'Brochure';
      node.appendChild(label);
    } else {
      node.textContent = value.display;
      // No value yet (e.g. no CRM contact in To): the editor shows the
      // variable's name instead of an invisible pill; the html stays empty.
      if (value.display === '') {
        node.setAttribute('data-ra-var-empty', '');
      }
    }
    return node;
  }

  static value(node: HTMLElement): VarValue {
    return {
      id: node.getAttribute('data-ra-var') ?? '',
      display: getVarDisplay(node),
    };
  }

  html(): string {
    const node = this.domNode as HTMLElement;
    const id = node.getAttribute('data-ra-var') ?? '';
    const display = getVarDisplay(node, this.contentNode).replaceAll('﻿', '');
    return `<span data-ra-var="${escapeAttr(id)}">${escapeText(
      display,
    )}</span>`;
  }
}

// Loading box holding a dropped/pasted image's slot until its upload resolves.
export class ImagePlaceholderBlot extends InlineEmbed {
  static blotName = 'ra-image-placeholder';
  static tagName = 'SPAN';
  static className = 'ra-image-placeholder';

  static create(value: string): HTMLElement {
    const node = super.create(value) as HTMLElement;
    node.setAttribute('data-ra-placeholder', value);
    node.setAttribute('contenteditable', 'false');
    return node;
  }

  static value(node: HTMLElement): string {
    return node.getAttribute('data-ra-placeholder') ?? '';
  }

  // Empty: never leaks into a sent email, and keeps the editor value unchanged
  // mid-upload, so a host that writes the html back cannot re-seed the document
  // and destroy the placeholder (which would silently lose the image).
  html(): string {
    return '';
  }
}

export class CustomHtmlBlot extends BlockEmbed {
  static blotName = 'ra-custom-html';
  static tagName = 'DIV';
  static className = 'ra-custom-html';

  static create(value: string): HTMLElement {
    const node = super.create(value) as HTMLElement;
    node.setAttribute('data-ra-custom-html', value);
    node.setAttribute('contenteditable', 'false');

    const pill = document.createElement('div');
    pill.className = 'ra-custom-html-pill';
    pill.textContent = '...';

    const body = document.createElement('div');
    body.className = 'ra-custom-html-body';
    body.style.display = 'none';
    body.innerHTML = sanitizeHtml(decodeURIComponent(value));

    pill.addEventListener('click', () => {
      const collapsed = body.style.display === 'none';
      body.style.display = collapsed ? 'block' : 'none';
      pill.style.display = 'inline-block';
      pill.textContent = collapsed ? '▲' : '...';
    });

    node.appendChild(pill);
    node.appendChild(body);
    return node;
  }

  static value(node: HTMLElement): string {
    return node.getAttribute('data-ra-custom-html') ?? '';
  }

  html(): string {
    return blockEmbedHtml(this.domNode as HTMLElement, 'data-ra-custom-html');
  }
}

export class SignatureBlot extends BlockEmbed {
  static blotName = 'ra-signature';
  static tagName = 'DIV';
  static className = 'ra-signature';

  static create(value: string): HTMLElement {
    const node = super.create(value) as HTMLElement;
    node.setAttribute('data-ra-signature', value);
    node.setAttribute('contenteditable', 'false');
    node.innerHTML = sanitizeHtml(decodeURIComponent(value));
    return node;
  }

  static value(node: HTMLElement): string {
    return node.getAttribute('data-ra-signature') ?? '';
  }

  html(): string {
    return blockEmbedHtml(this.domNode as HTMLElement, 'data-ra-signature');
  }
}

let registered = false;

export const registerQuillBlots = (): void => {
  if (registered) {
    return;
  }
  Quill.register(
    {
      [`formats/${SnippetBlot.blotName}`]: SnippetBlot,
      [`formats/${VarBlot.blotName}`]: VarBlot,
      [`formats/${ImagePlaceholderBlot.blotName}`]: ImagePlaceholderBlot,
      [`formats/${CustomHtmlBlot.blotName}`]: CustomHtmlBlot,
      [`formats/${SignatureBlot.blotName}`]: SignatureBlot,
    },
    true,
  );
  registered = true;
};
